import { z } from "zod";
import { defineEndpoint } from "./endpoint.js";
import { shopProductCardSchema, storefrontHostSchema } from "./storefront.js";
/**
 * Product reviews and wishlists of the online store (B-87, SF-19, SF-20; owner decision 2026-10-30). Only a shopper
 * with a delivered order of the product reviews it, once per order; the shop moderates. Wishlists belong to a signed-in
 * shopper.
 */
const tag = { tag: "storefront" };
const pub = { public: true };
const host = storefrontHostSchema;
const session = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
export const REVIEW_STATUSES = ["pending", "published", "hidden"];
export const REVIEW_AUTO_PUBLISH = ["none", "good", "all"];
export const publicReviewSchema = z.object({
    id: z.uuid(),
    rating: z.int().min(1).max(5),
    text: z.string(),
    /** The shopper's first name only. */
    author: z.string(),
    createdAt: z.iso.datetime(),
    reply: z.string().nullable(),
});
export const productReviewsSchema = z.object({
    /** Average of published reviews (one decimal), null when none. */
    average: z.number().nullable(),
    count: z.int(),
    /** How many published reviews have each rating, 1★ … 5★. */
    histogram: z.array(z.int()).length(5),
    data: z.array(publicReviewSchema),
    nextCursor: z.uuid().nullable(),
});
export const listShopReviews = defineEndpoint({
    ...tag,
    name: "listShopReviews",
    method: "GET",
    path: "/v1/storefront/products/{slug}/reviews",
    access: pub,
    summary: "A product's published reviews, newest first, with the average and the rating counts",
    params: z.object({ slug: z.string().min(1).max(200) }),
    query: z.object({ host, cursor: z.uuid().optional() }),
    status: 200,
    response: productReviewsSchema,
});
export const reviewableSchema = z.object({
    productId: z.uuid(),
    slug: z.string(),
    name: z.string(),
    orderNumber: z.string(),
});
export const listReviewable = defineEndpoint({
    ...tag,
    name: "listReviewable",
    method: "POST",
    path: "/v1/storefront/account/reviewable",
    access: pub,
    summary: "Products the signed-in shopper received and has not reviewed yet",
    body: z.object({ host, token: session }).strict(),
    status: 200,
    response: z.object({ data: z.array(reviewableSchema) }),
    errors: ["UNAUTHENTICATED"],
});
export const submitShopReview = defineEndpoint({
    ...tag,
    name: "submitShopReview",
    method: "POST",
    path: "/v1/storefront/account/reviews",
    access: pub,
    summary: "Review a product the signed-in shopper received (once per order)",
    body: z
        .object({
        host,
        token: session,
        productId: z.uuid(),
        rating: z.int().min(1).max(5),
        text: z.string().trim().max(2000).default(""),
    })
        .strict(),
    status: 201,
    response: z.object({ status: z.enum(REVIEW_STATUSES) }),
    errors: ["UNAUTHENTICATED", "BUSINESS_RULE_VIOLATION"],
});
// Wishlist (SF-20) ---------------------------------------------------------------------------------------------------
export const shopWishlist = defineEndpoint({
    ...tag,
    name: "shopWishlist",
    method: "POST",
    path: "/v1/storefront/account/wishlist",
    access: pub,
    summary: "The signed-in shopper's saved products (published ones only)",
    body: z.object({ host, token: session }).strict(),
    status: 200,
    response: z.object({ data: z.array(shopProductCardSchema) }),
    errors: ["UNAUTHENTICATED"],
});
export const setShopWishlist = defineEndpoint({
    ...tag,
    name: "setShopWishlist",
    method: "POST",
    path: "/v1/storefront/account/wishlist/items",
    access: pub,
    summary: "Save or remove products (guest saves are merged on sign-in by sending them here)",
    body: z
        .object({ host, token: session, productIds: z.array(z.uuid()).min(1).max(100), saved: z.boolean() })
        .strict(),
    status: 200,
    response: z.object({ productIds: z.array(z.uuid()) }),
    errors: ["UNAUTHENTICATED"],
});
// Admin: moderation -----------------------------------------------------------------------------------------------
export const adminReviewSchema = z.object({
    id: z.uuid(),
    productId: z.uuid(),
    productName: z.string(),
    orderNumber: z.string(),
    phone: z.string(),
    author: z.string(),
    rating: z.int(),
    text: z.string(),
    status: z.enum(REVIEW_STATUSES),
    reply: z.string().nullable(),
    createdAt: z.iso.datetime(),
    version: z.int(),
});
export const listAdminReviews = defineEndpoint({
    ...tag,
    name: "listAdminReviews",
    method: "GET",
    path: "/v1/storefront-reviews",
    access: { permission: "storefront.view" },
    summary: "Reviews to moderate, newest first",
    query: z.object({
        status: z.enum(REVIEW_STATUSES).optional(),
        limit: z.coerce.number().int().min(1).max(100).default(50),
        cursor: z.uuid().optional(),
    }),
    status: 200,
    response: z.object({ data: z.array(adminReviewSchema), nextCursor: z.uuid().nullable() }),
});
export const moderateReviewSchema = z
    .object({
    status: z.enum(["published", "hidden"]),
    reply: z.string().trim().max(1000).nullable().optional(),
    version: z.int().min(1),
})
    .strict();
export const moderateReview = defineEndpoint({
    ...tag,
    name: "moderateReview",
    method: "POST",
    path: "/v1/storefront-reviews/{id}/moderate",
    access: { permission: "storefront.manage" },
    summary: "Publish or hide a review, with an optional reply from the shop",
    params: z.object({ id: z.uuid() }),
    body: moderateReviewSchema,
    status: 200,
    response: adminReviewSchema,
    errors: ["CONFLICT"],
});
//# sourceMappingURL=storefront-reviews.js.map